import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { SortOption } from '@shared/constants/accommodations';
import type { PriceSource } from '@shared/constants/accommodations';
import type { Maybe, TravelOfferProvider } from '@graphql/generated-base';

export const listingsPanelTelemetry = createTelemetryObject({
  actions: {
    /**
     * Fires when a guest opens a hotel from a listing tile, a map marker popup or the tile CTA.
     *
     * `travelOfferProvider` names the provider whose price the tile displayed. `priceSource` names
     * the fetch path that produced it. The tile can show a snapshot price from one provider. The
     * detail page can then show a live price from a different provider. This happens because
     * travel_rate_snapshot holds one row per joyPlace, regardless of provider. Together the two
     * attributes make that switch measurable across a click.
     */
    seeAvailabilityClicked: ({
      joyPlaceId,
      hotelName,
      isSponsored,
      chain,
      isCoupleFavourite,
      priceSource,
      travelOfferProvider,
      name = 'seeAvailabilityClicked',
    }: {
      joyPlaceId?: string;
      hotelName?: string;
      isSponsored?: boolean;
      chain?: string | null;
      isCoupleFavourite?: boolean;
      /** Undefined when the tile showed no provider price — a room block, or a price still loading. */
      priceSource?: PriceSource;
      /** Undefined when the redirect books no specific rate, such as a sold-out hotel search fallback. */
      travelOfferProvider?: TravelOfferProvider;
      name?: string;
    }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name,
        joyPlaceId,
        from: 'Accommodations Console',
        hotelName,
        isSponsored,
        chain,
        isCoupleFavourite,
        priceSource,
        travelOfferProvider,
      },
    }),
    hotelBookingRedirect: ({
      joyPlaceId,
      hotelName,
      destinationURL,
      destinationDomain,
      destinationName,
      from = 'Accommodations Console',
      name = 'hotelBookingRedirect',
      isSponsored,
      chain,
      isCoupleFavourite,
      utm_source,
      utm_medium,
      utm_campaign,
    }: {
      joyPlaceId?: string;
      hotelName?: string;
      destinationURL: string;
      destinationDomain: string;
      destinationName: string;
      from?: string;
      name?: string;
      isSponsored?: boolean;
      chain?: string | null;
      isCoupleFavourite?: boolean;
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
    }) => ({
      category: 'travel',
      action: 'GuestHotelBookingRedirect',
      label: 'hotelSearch',
      extraInfo: {
        name,
        joyPlaceId,
        hotelName,
        destinationURL: destinationURL || '',
        destinationDomain: destinationDomain || '',
        destinationName: destinationName || '',
        from,
        isSponsored,
        chain,
        isCoupleFavourite,
        utm_source,
        utm_medium,
        utm_campaign,
      },
    }),
    promotedHotelImpression: ({ joyPlaceId, hotelName, chain }: { joyPlaceId: string; hotelName: string; chain?: string | null }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'promotedHotelImpression',
        joyPlaceId,
        hotelName,
        chain,
        from: 'Accommodations Console',
      },
    }),
    pickedByCoupleToggled: ({ state }: { state: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'pickedByCoupleToggled',
        state,
        from: 'Accommodations Console',
      },
    }),
    joyTravelBenefitsFilterChanged: ({ action, popular }: { action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'joyTravelBenefitsFilterChanged',
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    freeCancellationFilterChanged: ({ action, popular }: { action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'freeCancellationFilterChanged',
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    showAvailableOnlyFilterChanged: ({ action }: { action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'showAvailableOnlyFilterChanged',
        action,
        from: 'Accommodations Console',
      },
    }),
    // Cash-fund variant: "Save more, travel further" toggle (show only fund-covered stays). TRAV-3348.
    cashFundCoveredFilterChanged: ({ action }: { action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'cashFundCoveredFilterChanged',
        action,
        from: 'Accommodations Console',
      },
    }),
    // Cash-fund variant: dismissing the "Your Honeymoon Fund Goes Further" banner. TRAV-3348.
    cashFundBannerDismissed: () => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'cashFundBannerDismissed',
        from: 'Accommodations Console',
      },
    }),
    guestRatingFilterChanged: ({ rating, action, popular }: { rating: number; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'guestRatingFilterChanged',
        rating,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    starRatingFilterChanged: ({ rating, action }: { rating: number; action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'starRatingFilterChanged',
        rating,
        action,
        from: 'Accommodations Console',
      },
    }),
    facilityFilterChanged: ({ facility, action, popular }: { facility: string; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'facilityFilterChanged',
        facility,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    propertyCategoryFilterChanged: ({ propertyCategory, action, popular }: { propertyCategory: string; action: 'added' | 'removed'; popular?: boolean }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'propertyCategoryFilterChanged',
        propertyCategory,
        action,
        from: 'Accommodations Console',
        ...(popular && { popular }),
      },
    }),
    brandFilterChanged: ({ brand, action }: { brand: string; action: 'added' | 'removed' }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'brandFilterChanged',
        brand,
        action,
        from: 'Accommodations Console',
      },
    }),
    priceRangeFilterChanged: ({ minPrice, maxPrice }: { minPrice: number; maxPrice: number }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'priceRangeFilterChanged',
        minPrice,
        maxPrice,
        from: 'Accommodations Console',
      },
    }),
    allFiltersReset: () => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'allFiltersReset',
        from: 'Accommodations Console',
      },
    }),
    propertyNameSearched: ({ keyword, resultCount }: { keyword: string; resultCount: number }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'propertyNameSearched',
        keyword,
        resultCount,
        from: 'Accommodations Console',
      },
    }),
    sortOptionChanged: ({ sortOption }: { sortOption: SortOption }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'sortOptionChanged',
        sortOption,
        from: 'Accommodations Console',
      },
    }),
    destinationSearched: ({
      googlePlaceId,
      locationName,
      responseTimeMs,
      resultCount,
    }: {
      googlePlaceId?: Maybe<string>;
      locationName?: Maybe<string>;
      responseTimeMs: number;
      resultCount: number;
    }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'destinationSearched',
        googlePlaceId,
        locationName,
        responseTimeMs,
        resultCount,
        from: 'Accommodations Console',
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(listingsPanelTelemetry, { eventId: '', section: '' });

export { TelemetryProvider, useTelemetry as useListingsPanelTelemetry };
